/* eslint-disable @typescript-eslint/ban-ts-comment, complexity */
import { datadogRum, RumActionEvent, RumEvent } from '@datadog/browser-rum-slim'
import { TrackingKey } from '@evenfinancial/even-ts-static'

import {
  baseConsumerUrl,
  basePartnerpageUrl,
  brandFromHost,
  companyUuid,
  ddRUM as ddRUMConfig,
} from 'embed/constants/config'
import { REDACTED_VALUE } from 'embed/constants/pii-handling'
import Session from 'embed/services/Session'

import { isDataDogSampleRate } from '../types'
import { DEFAULT_SAMPLE_RATE } from '../constants'
import {
  containsPossibleStreetAddress,
  redactPossiblePiiParamsFromQueryObject,
  redactPossiblePiiParamsFromUrlObjectKeyValue,
} from '../utils'
import { RUMContextKeys } from './constants'

datadogRum.init({
  beforeSend(event: RumEvent) {
    if (event.action) {
      const action = (event as RumActionEvent).action
      // Will be redacting street addresses present in an action name
      if (
        action.name === 'string' &&
        action.name.startsWith('click on') &&
        containsPossibleStreetAddress(action.name)
      ) {
        action.name = `click on ${REDACTED_VALUE}`
      }
      // Will be redacting street addresses present in an action target name
      if (typeof action.target?.name === 'string' && containsPossibleStreetAddress(action.target.name)) {
        action.target.name = REDACTED_VALUE
      }
      // @ts-ignore
      redactPossiblePiiParamsFromUrlObjectKeyValue(event.action, 'referrer')
      // @ts-ignore
      redactPossiblePiiParamsFromUrlObjectKeyValue(event.action, 'url')
    }
    if (event.view) {
      redactPossiblePiiParamsFromUrlObjectKeyValue(event.view, 'referrer')
      redactPossiblePiiParamsFromUrlObjectKeyValue(event.view, 'url')
      // @ts-ignore
      if (event.view.url_query?.app) {
        // @ts-ignore
        event.view.url_query = redactPossiblePiiParamsFromQueryObject(event.view.url_query)
      }
    }
    if (event.session) {
      // @ts-ignore
      redactPossiblePiiParamsFromUrlObjectKeyValue(event.session, 'referrer')
      redactPossiblePiiParamsFromUrlObjectKeyValue(event.session, 'url')
      if (event.session.initial_view) {
        // @ts-ignore
        redactPossiblePiiParamsFromUrlObjectKeyValue(event.session.initial_view, 'url')
        // @ts-ignore
        if (event.session.initial_view?.url_query?.app) {
          // @ts-ignore
          event.session.initial_view.url_query = redactPossiblePiiParamsFromQueryObject(
            // @ts-ignore
            event.session.initial_view.url_query,
          )
        }
      }
    }
    if (event.resource) {
      // @ts-ignore
      redactPossiblePiiParamsFromUrlObjectKeyValue(event.resource, 'referrer')
      // @ts-ignore
      redactPossiblePiiParamsFromUrlObjectKeyValue(event.resource, 'url')
      // @ts-ignore
      if (event.resource?.url_query?.app) {
        // @ts-ignore
        event.resource.url_query = redactPossiblePiiParamsFromQueryObject(event.resource.url_query)
      }
    }
    if (event.error) {
      // @ts-ignore
      if (event.error.resource) {
        // @ts-ignore
        redactPossiblePiiParamsFromUrlObjectKeyValue(event.error.resource, 'referrer')
        // @ts-ignore
        redactPossiblePiiParamsFromUrlObjectKeyValue(event.error.resource, 'url')
        // @ts-ignore
        if (event.error.resource.url_query?.app) {
          // @ts-ignore
          event.error.resource.url_query = redactPossiblePiiParamsFromQueryObject(event.error.resource.url_query)
        }
      }
    }
    // Adding additional context that isn't reliably captured ahead of time
    const sessionUuid = Session.sessionUuid
    const leadUuid = Session.getLeadUuid()
    let updatedEventContext
    if (sessionUuid && event.context?.[RUMContextKeys.DD_CTX_SESSION_UUID] !== sessionUuid) {
      datadogRum.addRumGlobalContext(RUMContextKeys.DD_CTX_SESSION_UUID, sessionUuid)
      updatedEventContext = event.context
        ? { ...event.context, [RUMContextKeys.DD_CTX_SESSION_UUID]: sessionUuid }
        : { [RUMContextKeys.DD_CTX_SESSION_UUID]: sessionUuid }
    }
    if (leadUuid && event.context?.[RUMContextKeys.DD_CTX_LEAD_UUID] !== leadUuid) {
      datadogRum.addRumGlobalContext(RUMContextKeys.DD_CTX_LEAD_UUID, leadUuid)
      if (updatedEventContext) {
        updatedEventContext[RUMContextKeys.DD_CTX_LEAD_UUID] = leadUuid
      } else {
        updatedEventContext = event.context
          ? { ...event.context, [RUMContextKeys.DD_CTX_LEAD_UUID]: leadUuid }
          : { [RUMContextKeys.DD_CTX_LEAD_UUID]: leadUuid }
      }
    }
    if (updatedEventContext) {
      event.context = updatedEventContext
    }
  },
  trackInteractions: true,
  trackSessionAcrossSubdomains: true,
  useCrossSiteSessionCookie: true,
  useSecureSessionCookie: true,
  ...ddRUMConfig,
  sampleRate: isDataDogSampleRate(ddRUMConfig.sampleRate) ? ddRUMConfig.sampleRate : DEFAULT_SAMPLE_RATE,
})

if (baseConsumerUrl) {
  datadogRum.addRumGlobalContext(RUMContextKeys.DD_CTX_BASE_URL_CONSUMER, baseConsumerUrl)
}

if (basePartnerpageUrl) {
  datadogRum.addRumGlobalContext(RUMContextKeys.DD_CTX_BASE_URL_PARTNERPAGE, basePartnerpageUrl)
}

if (brandFromHost) {
  datadogRum.addRumGlobalContext(RUMContextKeys.DD_CTX_BRAND_FROM_HOST, brandFromHost)
}

if (companyUuid) {
  datadogRum.addRumGlobalContext(RUMContextKeys.DD_CTX_COMPANY_UUID, companyUuid)
}

export function removeRumGlobalContext(contextKey: string): void {
  const globalContext = datadogRum.getRumGlobalContext()
  if (contextKey in globalContext) {
    datadogRum.setRumGlobalContext(
      Object.entries(globalContext).reduce((newContext: typeof globalContext, [key, value]) => {
        if (key !== contextKey) {
          newContext[key] = value
        }
        return newContext
      }, {}),
    )
  }
}

export function sendValidationErrorOnBlur(actionData): void {
  datadogRum.addAction(TrackingKey.ValidationErrorOnBlur, actionData)
}

export { datadogRum }
